/**
 * Setup resources:
 *   /rate-cards       GET ?cropId&stream&status&orgId&inForceOn · POST · PATCH /:id · POST /:id/publish · /:id/retire · DELETE /:id
 *   /sack-types       GET ?active · POST { code, name, tareKg, active? } · PATCH /:id · DELETE /:id
 *   /crop-yield-caps  GET ?cropId&district · POST { cropId, district?, maxKgPerAcre, flatCapKgPerFarmerSeason?, source } · PATCH · DELETE
 *   /equipment        GET ?gateKind&category&locationId&isActive · POST · PATCH /:id · DELETE /:id
 *   /devices          GET ?groupId&locationId&revoked · POST { deviceUid, groupId?|locationId?, operatorId?, codePrefix? }
 *                     PATCH /:id { operatorId } · POST /:id/revoke { reason? } · GET /:id/team
 *   /groups/:id/team  POST { role, userId? | firstName, lastName?, contactNo? } · DELETE /:userId
 *   /users/:id/pin    POST { pin } · DELETE
 * Entities come back camelCase (MikroORM serialisation); kg and ₹ as decimal strings.
 */
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api'
import { istDateKey } from '@/lib/ist'
import { getOne, getPage, useConsoleMutation, usePageQuery, type Page, type PageParams } from './core'
import { ck } from './keys'
import { arr, bool, inrToPaise0, iso, kgToGrams0, num, obj, personName, relId, str, strOrNull } from './mappers'
import type { DeductionRule } from '../model/lotMath'
import type { DeviceBody } from '../model/deviceForm'
import type { VillageTeamBody } from '../model/teamForm'
import type { EquipmentBody } from '../model/equipmentForm'

// ============================== rate cards ==============================

export interface RateCardRow {
  id: string
  cropId: string | null
  cropName: string | null
  orgId: string | null
  stream: string
  version: number
  status: string
  effectiveFrom: string
  effectiveTo: string | null
  basePricePerKg: string
  pricePaisePerKg: number
  deductionRules: DeductionRule[]
  publishedById: string | null
  publishedByName: string | null
  publishedAt: string | null
}
export const mapRateCard = (r: Record<string, unknown>): RateCardRow => ({
  id: str(r.id),
  cropId: relId(r.crop),
  cropName: strOrNull(obj(r.crop).name),
  orgId: relId(r.org),
  stream: str(r.stream, 'MAIN'),
  version: num(r.version, 1),
  status: str(r.status, 'DRAFT'),
  effectiveFrom: str(r.effectiveFrom),
  effectiveTo: strOrNull(r.effectiveTo),
  basePricePerKg: str(r.basePricePerKg, '0'),
  pricePaisePerKg: inrToPaise0(r.basePricePerKg as string),
  deductionRules: arr(r.deductionRules) as unknown as DeductionRule[],
  publishedById: relId(r.publishedBy),
  publishedByName: personName(r.publishedBy),
  publishedAt: iso(r.publishedAt),
})

/** In force today (IST): published and today within [from, to]. */
export function rateCardInForce(c: Pick<RateCardRow, 'status' | 'effectiveFrom' | 'effectiveTo'>, today = istDateKey()): boolean {
  return c.status === 'PUBLISHED' && c.effectiveFrom <= today && (!c.effectiveTo || c.effectiveTo >= today)
}

export interface RateCardFilters extends PageParams {
  cropId?: string
  stream?: string
  status?: string
}
export function useRateCards(f: RateCardFilters) {
  return usePageQuery([...ck.rateCards, 'list', f], () => getPage('/rate-cards', f, mapRateCard))
}
export function useRateCard(id: string | null | undefined) {
  return usePageQuery(
    [...ck.rateCards, 'one', id],
    async () => {
      const r = obj(await api.get(`/rate-cards/${id}`))
      const row = mapRateCard(r)
      return { rows: [row], total: 1, page: 1, limit: 1, totalPages: 1 }
    },
    { enabled: Boolean(id), staleTime: 5 * 60_000 },
  )
}

export interface RateCardBody {
  cropId?: string
  stream?: string
  basePricePerKg?: string
  deductionRules?: DeductionRule[]
  effectiveFrom?: string
  effectiveTo?: string | null
}
/** `inline`: the form shows the refusal itself (no toast). */
export function useSaveRateCard({ inline = false } = {}) {
  return useConsoleMutation<{ id?: string; body: RateCardBody }>({
    mutationFn: ({ id, body }) => (id ? api.patch(`/rate-cards/${id}`, body) : api.post('/rate-cards', body)),
    invalidate: [ck.rateCards],
    success: (_d, v) => (v.id ? 'Draft saved' : 'Draft card created'),
    errorTitle: 'Rate card not saved',
    onError: () => inline,
  })
}
export function usePublishRateCard() {
  return useConsoleMutation<string>({
    mutationFn: (id) => api.post(`/rate-cards/${id}/publish`),
    invalidate: [ck.rateCards],
    success: 'Published — phones get it with their next farmer list update',
    errorTitle: 'Not published',
  })
}
export function useRetireRateCard() {
  return useConsoleMutation<string>({
    mutationFn: (id) => api.post(`/rate-cards/${id}/retire`),
    invalidate: [ck.rateCards],
    success: 'Card retired',
    errorTitle: 'Not retired',
  })
}
export function useDeleteRateCard() {
  return useConsoleMutation<string>({
    mutationFn: (id) => api.delete(`/rate-cards/${id}`),
    invalidate: [ck.rateCards],
    success: 'Draft deleted',
    errorTitle: 'Not deleted',
  })
}

// ============================== sack types ==============================

export interface SackTypeRow {
  id: string
  code: string
  name: string
  tareKg: string
  tareG: number
  active: boolean
}
export const mapSackType = (r: Record<string, unknown>): SackTypeRow => ({
  id: str(r.id),
  code: str(r.code),
  name: str(r.name),
  tareKg: str(r.tareKg ?? r.tare_kg, '0'),
  tareG: kgToGrams0((r.tareKg ?? r.tare_kg) as string),
  active: r.active === undefined ? true : bool(r.active),
})
export function useSackTypes(f: PageParams & { active?: boolean } = { page: 1, limit: 200 }) {
  return usePageQuery([...ck.sackTypes, 'list', f], () => getPage('/sack-types', f, mapSackType), {
    staleTime: 5 * 60_000,
  })
}
/** `inline`: the form shows the refusal itself (no toast). */
export function useSaveSackType({ inline = false } = {}) {
  return useConsoleMutation<{ id?: string; body: { code?: string; name?: string; tareKg?: string; active?: boolean } }>({
    mutationFn: ({ id, body }) => (id ? api.patch(`/sack-types/${id}`, body) : api.post('/sack-types', body)),
    invalidate: [ck.sackTypes],
    success: (_d, v) =>
      !v.id ? 'Sack type added' : v.body.active === undefined ? 'Sack type saved' : v.body.active ? 'Sack type in use again' : 'Sack type no longer offered',
    errorTitle: 'Sack type not saved',
    onError: () => inline,
  })
}
export function useDeleteSackType() {
  return useConsoleMutation<string>({
    mutationFn: (id) => api.delete(`/sack-types/${id}`),
    invalidate: [ck.sackTypes],
    success: 'Sack type removed',
  })
}

// ============================== yield caps ==============================

export interface YieldCapRow {
  id: string
  cropId: string | null
  cropName: string | null
  district: string | null
  maxKgPerAcre: string
  flatCapKgPerFarmerSeason: string | null
  source: string
}
export const mapYieldCap = (r: Record<string, unknown>): YieldCapRow => ({
  id: str(r.id),
  cropId: relId(r.crop),
  cropName: strOrNull(obj(r.crop).name),
  district: strOrNull(r.district),
  maxKgPerAcre: str(r.maxKgPerAcre),
  flatCapKgPerFarmerSeason: strOrNull(r.flatCapKgPerFarmerSeason),
  source: str(r.source),
})
export function useYieldCaps(f: PageParams & { cropId?: string; district?: string }) {
  return usePageQuery([...ck.yieldCaps, 'list', f], () => getPage('/crop-yield-caps', f, mapYieldCap))
}
/** `inline`: the form shows the refusal itself (no toast). */
export function useSaveYieldCap({ inline = false } = {}) {
  return useConsoleMutation<{
    id?: string
    body: { cropId?: string; district?: string; maxKgPerAcre?: string; flatCapKgPerFarmerSeason?: string | null; source?: string }
  }>({
    mutationFn: ({ id, body }) => (id ? api.patch(`/crop-yield-caps/${id}`, body) : api.post('/crop-yield-caps', body)),
    invalidate: [ck.yieldCaps],
    success: 'Yield cap saved',
    errorTitle: 'Yield cap not saved',
    onError: () => inline,
  })
}
export function useDeleteYieldCap() {
  return useConsoleMutation<string>({
    mutationFn: (id) => api.delete(`/crop-yield-caps/${id}`),
    invalidate: [ck.yieldCaps],
    success: 'Yield cap removed',
  })
}

// ============================== equipment ==============================

export interface EquipmentRow {
  id: string
  code: string
  name: string
  category: string
  gateKind: string | null
  serialNo: string | null
  isActive: boolean
  locationId: string | null
  calibratedAt: string | null
  calibrationDueAt: string | null
  legalMetrologyCertNo: string | null
  stampedAt: string | null
  stampDueAt: string | null
  sharedUse: boolean
  description: string | null
  /** Free-form JSON; machines keep their steps in step_codes (F6.9). */
  specifications: Record<string, unknown> | null
}
export const mapEquipment = (r: Record<string, unknown>): EquipmentRow => ({
  id: str(r.id),
  code: str(r.code),
  name: str(r.name),
  category: str(r.category, 'MEASUREMENT'),
  gateKind: strOrNull(r.gateKind),
  serialNo: strOrNull(r.serialNo),
  isActive: r.isActive === undefined ? true : bool(r.isActive),
  locationId: relId(r.location),
  calibratedAt: strOrNull(r.calibratedAt),
  calibrationDueAt: strOrNull(r.calibrationDueAt),
  legalMetrologyCertNo: strOrNull(r.legalMetrologyCertNo),
  stampedAt: strOrNull(r.stampedAt),
  stampDueAt: strOrNull(r.stampDueAt),
  sharedUse: r.sharedUse === undefined ? true : bool(r.sharedUse),
  description: strOrNull(r.description),
  specifications: r.specifications && typeof r.specifications === 'object' ? obj(r.specifications) : null,
})

export type DueState = 'OK' | 'SOON' | 'LAPSED' | 'NONE'
/** Legal-metrology stamp / calibration date state (IST): lapsed, due within `soonDays`, ok. */
export function dueState(date: string | null, today = istDateKey(), soonDays = 30): DueState {
  if (!date) return 'NONE'
  const d = date.slice(0, 10)
  if (d < today) return 'LAPSED'
  const soon = new Date(`${today}T00:00:00Z`)
  soon.setUTCDate(soon.getUTCDate() + soonDays)
  return d <= soon.toISOString().slice(0, 10) ? 'SOON' : 'OK'
}

/**
 * uc-core reads a `=false` boolean query as true (implicit conversion runs before its @Transform),
 * so a "false" filter is applied here: fetch up to 200 without it, filter, and page locally.
 */
async function getPageWhereFalse<T, F extends PageParams>(
  path: string,
  f: F,
  key: keyof F,
  map: (r: Record<string, unknown>) => T,
  keep: (row: T) => boolean,
): Promise<Page<T>> {
  const { page = 1, limit = 20 } = f
  const all = await getPage(path, { ...f, [key]: undefined, page: 1, limit: 200 }, map)
  const rows = all.rows.filter(keep)
  return { rows: rows.slice((page - 1) * limit, page * limit), total: rows.length, page, limit, totalPages: Math.max(1, Math.ceil(rows.length / limit)) }
}

export function useEquipment(f: PageParams & { gateKind?: string; category?: string; isActive?: boolean }) {
  return usePageQuery([...ck.equipment, 'list', f], () =>
    f.isActive === false
      ? getPageWhereFalse('/equipment', f, 'isActive', mapEquipment, (r) => !r.isActive)
      : getPage('/equipment', f, mapEquipment),
  )
}
export function useSaveEquipment() {
  return useConsoleMutation<{ id?: string; body: EquipmentBody }>({
    mutationFn: ({ id, body }) => (id ? api.patch(`/equipment/${id}`, body) : api.post('/equipment', body)),
    invalidate: [ck.equipment],
    success: (_d, v) => (v.id ? 'Equipment saved' : 'Equipment registered'),
  })
}
export function useDeleteEquipment() {
  return useConsoleMutation<string>({
    mutationFn: (id) => api.delete(`/equipment/${id}`),
    invalidate: [ck.equipment],
    success: 'Equipment removed',
  })
}

// ============================== devices ==============================

export interface DeviceRow {
  id: string
  deviceUid: string
  codePrefix: string
  groupId: string | null
  groupName: string | null
  locationId: string | null
  locationName: string | null
  operatorId: string | null
  operatorName: string | null
  appVersion: string | null
  registeredAt: string | null
  revokedAt: string | null
  /** Typed by the office when revoking. */
  revokeReason: string | null
  status: 'ACTIVE' | 'REVOKED'
}
export const mapDevice = (r: Record<string, unknown>): DeviceRow => ({
  id: str(r.id),
  deviceUid: str(r.deviceUid),
  codePrefix: str(r.codePrefix),
  groupId: relId(r.group),
  groupName: strOrNull(obj(r.group).name),
  locationId: relId(r.location),
  locationName: strOrNull(obj(r.location).name ?? obj(r.location).code),
  operatorId: relId(r.operator),
  operatorName: personName(r.operator),
  appVersion: strOrNull(r.appVersion),
  registeredAt: iso(r.registeredAt),
  revokedAt: iso(r.revokedAt),
  revokeReason: strOrNull(r.revokeReason),
  status: r.revokedAt ? 'REVOKED' : 'ACTIVE',
})
export function useDevices(f: PageParams & { groupId?: string; locationId?: string; revoked?: boolean }) {
  return usePageQuery([...ck.devices, 'list', f], () =>
    f.revoked === false
      ? getPageWhereFalse('/devices', f, 'revoked', mapDevice, (r) => r.status === 'ACTIVE')
      : getPage('/devices', f, mapDevice),
  )
}
export function useDevice(id: string | null) {
  return useQuery({
    queryKey: [...ck.devices, 'one', id],
    enabled: Boolean(id),
    queryFn: () => getOne(`/devices/${id}`, mapDevice),
  })
}
export function useRegisterDevice() {
  return useConsoleMutation<DeviceBody, DeviceRow>({
    mutationFn: async (body) => mapDevice(obj(await api.post('/devices', body))),
    invalidate: [ck.devices],
    success: (d) => `Phone registered with code prefix ${d.codePrefix}`,
    errorTitle: 'Phone not registered',
  })
}
export function useUpdateDeviceOperator() {
  return useConsoleMutation<{ id: string; operatorId: string | null }>({
    mutationFn: ({ id, operatorId }) => api.patch(`/devices/${id}`, { operatorId }),
    invalidate: [ck.devices],
    success: 'Usual operator changed',
    errorTitle: 'Operator not changed',
  })
}
export function useRevokeDevice() {
  return useConsoleMutation<{ id: string; reason?: string }>({
    mutationFn: ({ id, reason }) => api.post(`/devices/${id}/revoke`, { reason: reason || undefined }),
    invalidate: [ck.devices],
    success: 'Phone revoked — it wipes its data on next contact',
    errorTitle: 'Phone not revoked',
  })
}

export interface DeviceTeamMember {
  userId: string
  name: string
  roles: string[]
  hasPin: boolean
}
/** GET /devices/:id/team — names, roles and whether a PIN is set. The PIN hashes are never kept. */
export function useDeviceTeam(id: string | null, enabled = true) {
  return useQuery({
    queryKey: [...ck.devices, 'team', id],
    enabled: Boolean(id) && enabled,
    queryFn: () =>
      getOne(`/devices/${id}/team`, (r) =>
        arr(r.team).map((m): DeviceTeamMember => ({
          userId: str(m.userId),
          name: str(m.name),
          roles: arr<string>(m.roles).map(String),
          hasPin: Boolean(m.pinHash),
        })),
      ),
  })
}

/** Who the village team changes reach: the phone's team list and the site team lists. */
const TEAM_KEYS = [ck.devices, ck.locations]

export function useAddVillageTeamMember() {
  return useConsoleMutation<{ groupId: string; body: VillageTeamBody }>({
    mutationFn: ({ groupId, body }) => api.post(`/groups/${groupId}/team`, body),
    invalidate: TEAM_KEYS,
    success: 'Added to the village team',
  })
}
export function useRemoveVillageTeamMember() {
  return useConsoleMutation<{ groupId: string; userId: string }>({
    mutationFn: ({ groupId, userId }) => api.delete(`/groups/${groupId}/team/${userId}`),
    invalidate: TEAM_KEYS,
    success: 'Removed from the village team',
  })
}
/** POST /users/:id/pin — the office sets a phone-only worker's PIN (pins:manage). */
export function useSetPin() {
  return useConsoleMutation<{ userId: string; pin: string }>({
    mutationFn: ({ userId, pin }) => api.post(`/users/${userId}/pin`, { pin }),
    invalidate: TEAM_KEYS,
    success: 'PIN saved. Phones get it with their next list update.',
    errorTitle: 'Could not save the PIN',
  })
}
export function useClearPin() {
  return useConsoleMutation<{ userId: string }>({
    mutationFn: ({ userId }) => api.delete(`/users/${userId}/pin`),
    invalidate: TEAM_KEYS,
    success: 'PIN cleared',
  })
}
